<script setup>
import { computed } from 'vue'
import { Badge } from '@drama/ui'

import AuditExperience from './AuditExperience.vue'
import AuditLaterRecords from './AuditLaterRecords.vue'
import AuditOpening from './AuditOpening.vue'
import AuditRecords from './AuditRecords.vue'
import { jobRange, useAuditText, variantOf } from './audit-text.js'
import { recordingNotes } from './run-observer.js'

const props = defineProps({
  pack: { type: Object, required: true },
  branch: { type: Object, required: true },
  sources: { type: Map, required: true },
  person: { type: Object, required: true },
  // characterRecord() 的结果。
  record: { type: Object, required: true },
  // 名字首字，没有头像图时当头像。
  initial: { type: String, required: true },
  location: { type: String, required: true },
})

const { tr, enumLabel, rangeLabel, memoryKindLabel, errorLabel } = useAuditText()
const appearance = computed(() => props.person.appearance || props.person.profile?.appearance || '—')
const laterCount = computed(() => props.record.latest_relations.length + props.record.goal_records.length + props.record.open_threads.length)
const memoryRows = computed(() => props.record.rolling_memory.map((summary) => ({
  key: summary.record_key,
  title: memoryKindLabel(summary),
  meta: `${rangeLabel(summary)} · ${tr('characters.memoryProgress', { count: summary.through_seq || 0 })}`,
  text: summary.text,
  record: summary,
})))
// 记忆整理任务按分支排队，待执行与失败的都列在滚动记忆里；整理未完成不代表人物不知道。
const memoryJobs = computed(() => recordingNotes(props.branch).memory_jobs)
</script>

<template>
  <section class="au-card" :data-testid="`character-card-${person.id}`">
    <header class="au-person__head">
      <img v-if="person.image" class="au-avatar au-avatar--lg" :src="person.image" :alt="person.name" />
      <span v-else class="au-avatar au-avatar--lg" aria-hidden="true">{{ initial }}</span>
      <div>
        <h2 class="au-card__title">{{ person.name }}</h2>
        <p class="au-hint">{{ location }}</p>
      </div>
    </header>
    <p v-if="person.public_profile" class="au-body">{{ person.public_profile }}</p>
    <dl class="au-kv">
      <dt>{{ tr('characters.appearance') }}</dt>
      <dd>{{ appearance }}</dd>
      <dt>{{ tr('characters.identity') }}</dt>
      <dd>{{ person.profile?.identity || '—' }}</dd>
      <dt>{{ tr('characters.initialGoal') }}</dt>
      <dd>{{ person.initial_goal || '—' }}</dd>
      <dt>{{ tr('characters.latestGoal') }}</dt>
      <dd>{{ record.current_goal || tr('empty') }}</dd>
    </dl>

    <details class="au-fold">
      <summary>{{ tr('characters.sections.opening') }}</summary>
      <AuditOpening :person="person" :record="record" />
    </details>
    <details class="au-fold" open>
      <summary>{{ tr('characters.sections.later') }} · {{ laterCount }}</summary>
      <AuditLaterRecords :record="record" :sources="sources" />
    </details>
    <details class="au-fold">
      <summary>{{ tr('characters.sections.witness') }} · {{ record.witnessed.length + record.observations.length }}</summary>
      <AuditExperience :pack="pack" :branch="branch" :record="record" :sources="sources" />
    </details>
    <details class="au-fold">
      <summary>{{ tr('characters.sections.memory') }} · {{ memoryRows.length }}</summary>
      <div class="au-fold__body">
        <ul v-if="memoryJobs.length" class="au-lines">
          <li v-for="job in memoryJobs" :key="job.job_id" class="au-chips">
            <Badge size="sm" :variant="variantOf(job.status)">{{ enumLabel('jobs.statuses', job.status) }}</Badge>
            <span>{{ enumLabel('jobs.kinds', job.kind || 'memory') }} {{ jobRange(job) }}</span>
            <span v-if="job.error_code" class="au-error" :title="job.error_code">{{ errorLabel(job.error_code) }}</span>
          </li>
        </ul>
        <AuditRecords :rows="memoryRows" :sources="sources" />
      </div>
    </details>
  </section>
</template>
